/**
 * HidingSpot - Interactive hiding spot entity for stealth gameplay
 * Allows player to hide from robots for a limited time
 */

import * as THREE from 'three';
import { Entity } from './Entity';
import type { Vector2 } from '../../types/game';
import type { Player } from './Player';

export type HidingSpotType = 'locker' | 'under_table' | 'closet';

export interface HidingSpotConfig {
  id: string;
  position: Vector2;
  size: { width: number; height: number };
  hideTime: number; // seconds until detected
  type: HidingSpotType;
}

export class HidingSpot extends Entity {
  // Hiding spot properties
  public hidingSpotId: string;
  public hideTime: number; // seconds until robots can detect player
  public spotType: HidingSpotType;

  // Interaction
  private interactionRadius: number = 40; // pixels
  private isPlayerHiding: boolean = false;
  private timeHidden: number = 0; // time player has been hiding
  private isCompromised: boolean = false; // if robots know player is here
  private isPlayerNearby: boolean = false;
  private mesh: THREE.Group | null = null;

  // Capacity
  private maxOccupants: number = 1;
  private currentOccupants: number = 0;

  constructor(config: HidingSpotConfig) {
    super(config.id, 'hiding_spot', config.position, config.size);

    this.hidingSpotId = config.id;
    this.hideTime = config.hideTime;
    this.spotType = config.type;

    // Set max occupants based on type
    this.maxOccupants = this.getMaxOccupantsByType(config.type);

    // Hiding spots don't have health
    this.health = -1;
    this.maxHealth = -1;

    // Create visual mesh
    this.createMesh();
  }

  /**
   * Get max occupants based on hiding spot type
   */
  private getMaxOccupantsByType(type: HidingSpotType): number {
    switch (type) {
      case 'locker':
        return 1;
      case 'under_table':
        return 2; // Can fit under larger tables
      case 'closet':
        return 1;
      default:
        return 1;
    }
  }

  /**
   * Create Three.js mesh for hiding spot
   */
  private createMesh(): void {
    this.mesh = new THREE.Group();

    switch (this.spotType) {
      case 'locker':
        this.createLockerMesh();
        break;
      case 'under_table':
        this.createTableMesh();
        break;
      case 'closet':
        this.createClosetMesh();
        break;
    }

    // Position the mesh
    this.mesh.position.set(this.position.x, this.position.y, 0);
  }

  /**
   * Create locker mesh (tall vertical box) - Metallic gray arcade/storage locker
   */
  private createLockerMesh(): void {
    if (!this.mesh) return;

    // Locker body - metallic gray
    const bodyGeometry = new THREE.BoxGeometry(this.size.width, this.size.height, 1);
    const bodyMaterial = new THREE.MeshBasicMaterial({ color: 0x4a4a5a });
    const bodyMesh = new THREE.Mesh(bodyGeometry, bodyMaterial);
    this.mesh.add(bodyMesh);

    // Locker door (slightly lighter metallic)
    const doorGeometry = new THREE.BoxGeometry(this.size.width - 4, this.size.height - 4, 1.5);
    const doorMaterial = new THREE.MeshBasicMaterial({ color: 0x5a5a6a });
    const doorMesh = new THREE.Mesh(doorGeometry, doorMaterial);
    this.mesh.add(doorMesh);

    // Locker vents (horizontal lines)
    for (let i = 0; i < 3; i++) {
      const y = -this.size.height / 4 + i * (this.size.height / 6);
      const lineGeometry = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(-this.size.width / 3, y, 2),
        new THREE.Vector3(this.size.width / 3, y, 2),
      ]);
      const lineMaterial = new THREE.LineBasicMaterial({ color: 0x2a2a3a });
      const line = new THREE.Line(lineGeometry, lineMaterial);
      this.mesh.add(line);
    }

    // Door handle - silver/chrome
    const handleGeometry = new THREE.BoxGeometry(3, 8, 2);
    const handleMaterial = new THREE.MeshBasicMaterial({ color: 0xc0c0c0 });
    const handleMesh = new THREE.Mesh(handleGeometry, handleMaterial);
    handleMesh.position.set(this.size.width / 3, 0, 2);
    this.mesh.add(handleMesh);
  }

  /**
   * Create table mesh (wide horizontal box with legs) - Pizza restaurant table
   */
  private createTableMesh(): void {
    if (!this.mesh) return;

    // Table top - red/maroon (classic pizza restaurant)
    const topGeometry = new THREE.BoxGeometry(this.size.width, 8, 1);
    const topMaterial = new THREE.MeshBasicMaterial({ color: 0x8b2500 });
    const topMesh = new THREE.Mesh(topGeometry, topMaterial);
    topMesh.position.y = this.size.height / 2 - 4;
    this.mesh.add(topMesh);

    // Checkered tablecloth pattern (optional visual)
    const clothGeometry = new THREE.BoxGeometry(this.size.width - 4, 4, 1.1);
    const clothMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const clothMesh = new THREE.Mesh(clothGeometry, clothMaterial);
    clothMesh.position.y = this.size.height / 2 - 2;
    this.mesh.add(clothMesh);

    // Table legs (4 corners) - dark wood
    const legWidth = 6;
    const legHeight = this.size.height - 8;
    const legGeometry = new THREE.BoxGeometry(legWidth, legHeight, 1);
    const legMaterial = new THREE.MeshBasicMaterial({ color: 0x3d2817 });

    const legPositions = [
      { x: -this.size.width / 2 + legWidth + 2, y: -6 },
      { x: this.size.width / 2 - legWidth - 2, y: -6 },
    ];

    legPositions.forEach(pos => {
      const legMesh = new THREE.Mesh(legGeometry, legMaterial);
      legMesh.position.set(pos.x, pos.y, 0);
      this.mesh!.add(legMesh);
    });

    // Shadow underneath (visual cue for hiding spot)
    const shadowGeometry = new THREE.BoxGeometry(this.size.width - 10, 6, 0.5);
    const shadowMaterial = new THREE.MeshBasicMaterial({ color: 0x0a0a0a, opacity: 0.5, transparent: true });
    const shadowMesh = new THREE.Mesh(shadowGeometry, shadowMaterial);
    shadowMesh.position.y = -this.size.height / 2 + 3;
    this.mesh.add(shadowMesh);
  }

  /**
   * Create closet mesh (medium sized box with door) - Storage/supply closet
   */
  private createClosetMesh(): void {
    if (!this.mesh) return;

    // Closet body - beige/cream color (typical supply closet)
    const bodyGeometry = new THREE.BoxGeometry(this.size.width, this.size.height, 1);
    const bodyMaterial = new THREE.MeshBasicMaterial({ color: 0x5a5a5a });
    const bodyMesh = new THREE.Mesh(bodyGeometry, bodyMaterial);
    this.mesh.add(bodyMesh);

    // Closet door (double doors) - gray metallic
    const doorWidth = (this.size.width - 6) / 2;
    const doorGeometry = new THREE.BoxGeometry(doorWidth, this.size.height - 4, 1.5);

    // Left door
    const leftDoorMaterial = new THREE.MeshBasicMaterial({ color: 0x6a6a6a });
    const leftDoorMesh = new THREE.Mesh(doorGeometry, leftDoorMaterial);
    leftDoorMesh.position.set(-doorWidth / 2 - 1, 0, 0.5);
    this.mesh.add(leftDoorMesh);

    // Right door
    const rightDoorMaterial = new THREE.MeshBasicMaterial({ color: 0x6a6a6a });
    const rightDoorMesh = new THREE.Mesh(doorGeometry, rightDoorMaterial);
    rightDoorMesh.position.set(doorWidth / 2 + 1, 0, 0.5);
    this.mesh.add(rightDoorMesh);

    // "SUPPLIES" label area (darker strip at top)
    const labelGeometry = new THREE.BoxGeometry(this.size.width - 8, 10, 1.6);
    const labelMaterial = new THREE.MeshBasicMaterial({ color: 0x3a3a3a });
    const labelMesh = new THREE.Mesh(labelGeometry, labelMaterial);
    labelMesh.position.set(0, this.size.height / 2 - 10, 0.6);
    this.mesh.add(labelMesh);

    // Door handles - silver/chrome
    const handleGeometry = new THREE.BoxGeometry(3, 8, 2);
    const handleMaterial = new THREE.MeshBasicMaterial({ color: 0xc0c0c0 });

    const leftHandle = new THREE.Mesh(handleGeometry, handleMaterial);
    leftHandle.position.set(-6, -5, 2);
    this.mesh.add(leftHandle);

    const rightHandle = new THREE.Mesh(handleGeometry, handleMaterial);
    rightHandle.position.set(6, -5, 2);
    this.mesh.add(rightHandle);
  }

  /**
   * Update hiding spot state
   */
  public update(deltaTime: number): void {
    if (this.destroyed || !this.active) return;

    // Update hide timer
    if (this.isPlayerHiding) {
      this.timeHidden += deltaTime;

      // Check if hiding time exceeded
      if (this.timeHidden >= this.hideTime) {
        this.compromise();
      }
    }

    // Update mesh position and visual state
    if (this.mesh) {
      this.mesh.position.set(this.position.x, this.position.y, 0);

      // Update color based on state (only update first child - the body mesh)
      const firstChild = this.mesh.children[0];
      if (firstChild instanceof THREE.Mesh) {
        const material = firstChild.material as THREE.MeshBasicMaterial;

        // Get base color based on hiding spot type (updated for pizza place theme)
        let baseColor = 0x5a5a5a; // default gray
        switch (this.spotType) {
          case 'locker':
            baseColor = 0x4a4a5a; // Metallic gray
            break;
          case 'closet':
            baseColor = 0x5a5a5a; // Gray closet
            break;
          case 'under_table':
            baseColor = 0x8b2500; // Red/maroon table
            break;
        }

        // Highlight when player nearby, red when compromised
        if (this.isCompromised) {
          material.color.setHex(0xff3333); // Red when compromised
        } else if (this.isPlayerNearby) {
          material.color.setHex(0x00cc66); // Green highlight when nearby (safe spot indicator)
        } else {
          material.color.setHex(baseColor); // Normal color
        }
      }
    }
  }

  /**
   * Render hiding spot (placeholder for Three.js rendering)
   */
  public render(): void {
    // Will be implemented with Three.js sprite rendering
  }

  /**
   * Check if player can interact with hiding spot
   */
  public canInteract(player: Player): boolean {
    if (this.destroyed || !this.active) {
      return false;
    }

    if (this.currentOccupants >= this.maxOccupants) {
      return false;
    }

    const distance = this.distanceTo(player);
    return distance <= this.interactionRadius;
  }

  /**
   * Hide player in this spot
   */
  public hide(player: Player): boolean {
    if (!this.canInteract(player)) {
      return false;
    }

    if (this.isPlayerHiding) {
      console.warn(`Player is already hiding in '${this.hidingSpotId}'`);
      return false;
    }

    this.isPlayerHiding = true;
    this.currentOccupants++;
    this.timeHidden = 0;
    this.isCompromised = false;

    console.log(`Player hiding in '${this.hidingSpotId}' (${this.spotType})`);
    return true;
  }

  /**
   * Exit hiding spot
   */
  public exit(): void {
    if (!this.isPlayerHiding) {
      return;
    }

    this.isPlayerHiding = false;
    this.currentOccupants = Math.max(0, this.currentOccupants - 1);
    this.timeHidden = 0;

    console.log(`Player exited hiding spot '${this.hidingSpotId}'`);
  }

  /**
   * Compromise the hiding spot (robots found the player)
   */
  private compromise(): void {
    if (this.isCompromised) {
      return;
    }

    this.isCompromised = true;
    console.log(`Hiding spot '${this.hidingSpotId}' compromised! Player detected!`);

    // Event will trigger robot detection logic
  }

  /**
   * Reset hiding spot (after player exits and robots leave)
   */
  public reset(): void {
    this.isCompromised = false;
    this.timeHidden = 0;
    console.log(`Hiding spot '${this.hidingSpotId}' reset`);
  }

  /**
   * Check if spot provides good cover
   */
  public isGoodCover(): boolean {
    // Good cover = not compromised and still has time left
    return !this.isCompromised && this.timeHidden < this.hideTime * 0.8;
  }

  /**
   * Get time remaining before detection
   */
  public getTimeRemaining(): number {
    if (!this.isPlayerHiding) {
      return this.hideTime;
    }

    return Math.max(0, this.hideTime - this.timeHidden);
  }

  /**
   * Get detection progress (0-1)
   */
  public getDetectionProgress(): number {
    if (!this.isPlayerHiding) {
      return 0;
    }

    return Math.min(1, this.timeHidden / this.hideTime);
  }

  // Getters
  public getIsPlayerHiding(): boolean {
    return this.isPlayerHiding;
  }

  public getIsCompromised(): boolean {
    return this.isCompromised;
  }

  public getTimeHidden(): number {
    return this.timeHidden;
  }

  public getSpotType(): HidingSpotType {
    return this.spotType;
  }

  public getCurrentOccupants(): number {
    return this.currentOccupants;
  }

  public getMaxOccupants(): number {
    return this.maxOccupants;
  }

  public isFull(): boolean {
    return this.currentOccupants >= this.maxOccupants;
  }

  public setPlayerNearby(nearby: boolean): void {
    this.isPlayerNearby = nearby;
  }

  public getMesh(): THREE.Group | null {
    return this.mesh;
  }

  /**
   * Get hiding spot info for UI/debugging
   */
  public getInfo(): {
    id: string;
    type: HidingSpotType;
    occupied: boolean;
    compromised: boolean;
    timeRemaining: number;
    detectionProgress: number;
    capacity: string;
  } {
    return {
      id: this.hidingSpotId,
      type: this.spotType,
      occupied: this.isPlayerHiding,
      compromised: this.isCompromised,
      timeRemaining: this.getTimeRemaining(),
      detectionProgress: this.getDetectionProgress(),
      capacity: `${this.currentOccupants}/${this.maxOccupants}`,
    };
  }
}
